import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Lightning, Circle, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";

let fbSdkLoaded = false;
function loadFbSdk(appId) {
  return new Promise((resolve) => {
    if (fbSdkLoaded && window.FB) return resolve();
    window.fbAsyncInit = () => {
      window.FB.init({ appId, autoLogAppEvents: true, xfbml: false, version: "v21.0" });
      fbSdkLoaded = true;
      resolve();
    };
    const s = document.createElement("script");
    s.src = "https://connect.facebook.net/en_US/sdk.js";
    s.async = true;
    s.defer = true;
    s.crossOrigin = "anonymous";
    document.body.appendChild(s);
  });
}

export const EmbeddedSignupButton = ({ onConnected }) => {
  const [status, setStatus] = useState(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    api.get("/whatsapp/embedded-signup/status").then((r) => setStatus(r.data)).catch(() => setStatus({ enabled: false }));
  }, []);

  const launch = async () => {
    if (!status?.enabled) return setInfoOpen(true);
    setConnecting(true);
    try {
      await loadFbSdk(status.app_id);
      let sessionInfo = { phone_number_id: null, waba_id: null };
      const listener = (event) => {
        if (!String(event.origin).endsWith("facebook.com")) return;
        try {
          const data = JSON.parse(event.data);
          if (data.type === "WA_EMBEDDED_SIGNUP" && data.event === "FINISH") {
            sessionInfo = { phone_number_id: data.data?.phone_number_id, waba_id: data.data?.waba_id };
          }
        } catch { /* non-JSON events ignored */ }
      };
      window.addEventListener("message", listener);
      window.FB.login(
        async (response) => {
          window.removeEventListener("message", listener);
          const code = response?.authResponse?.code;
          if (!code) { setConnecting(false); return toast.error("Conexion cancelada"); }
          try {
            const { data } = await api.post("/whatsapp/embedded-signup/exchange", {
              code, phone_number_id: sessionInfo.phone_number_id, waba_id: sessionInfo.waba_id,
            });
            toast.success(`Numero ${data.number?.display_phone || ""} conectado automaticamente`);
            onConnected();
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al completar la conexion");
          } finally { setConnecting(false); }
        },
        {
          config_id: status.config_id,
          response_type: "code",
          override_default_response_type: true,
          extras: { setup: {}, sessionInfoVersion: "3" },
        },
      );
    } catch {
      setConnecting(false);
      toast.error("No se pudo cargar el SDK de Meta");
    }
  };

  const steps = [
    { label: "Verificacion de negocio en Meta Business Manager", hint: "En curso segun indicaste" },
    { label: "Verificacion de dominio propio", hint: "Business Settings → Brand Safety → Domains" },
    { label: "App Review: whatsapp_business_messaging + management", hint: "Requiere videos demo del producto" },
    { label: "Aprobacion como Independent Tech Provider", hint: "Meta habilita el Embedded Signup" },
    { label: "Configurar META_APP_ID, META_APP_SECRET y META_ES_CONFIG_ID", hint: "En el servidor de Wazco Express (el codigo ya esta listo)" },
  ];

  return (
    <>
      <Button onClick={launch} disabled={connecting} className="bg-[#10B981] hover:bg-[#0EA271] text-white" data-testid="embedded-signup-button">
        {connecting ? <CircleNotch size={14} className="mr-1.5 animate-spin" /> : <Lightning size={14} className="mr-1.5" weight="fill" />}
        Conectar con Wazco Express
      </Button>

      <Dialog open={infoOpen} onOpenChange={setInfoOpen}>
        <DialogContent className="bg-[#121214] border-[#27272A] text-white max-w-lg" data-testid="embedded-signup-info">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Conexion 1-clic · pendiente de Meta</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-[#A1A1AA]">
            El flujo Embedded Signup (tu cliente conecta su WhatsApp sin ver tokens) ya esta programado en Wazco Express.
            Se activara automaticamente cuando Meta apruebe a Wazco Express como Tech Provider. Checklist de tramites:
          </p>
          <div className="space-y-2 mt-1">
            {steps.map((s, i) => (
              <div key={i} className="flex items-start gap-2.5 p-2.5 rounded-md bg-[#0A0A0C] border border-[#18181B]">
                {i === 0 ? <CircleNotch size={15} className="text-[#F59E0B] mt-0.5 shrink-0 animate-spin" /> : <Circle size={15} className="text-[#3F3F46] mt-0.5 shrink-0" />}
                <div>
                  <div className="text-xs font-medium">{s.label}</div>
                  <div className="text-[10px] text-[#71717A]">{s.hint}</div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-[#71717A]">
            Mientras tanto, usa <span className="text-white">Conexion manual</span>: el asistente guiado valida tus credenciales en vivo.
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
};
